import { Component, input, output, computed } from '@angular/core';


@Component({
  standalone: true,
  imports: [],
  selector: 'lib-refresh-button',
  template: `
    <button
      (click)="handleClick()"
      type="button"
      [disabled]="disabled() || isLoading()"
      [class]="buttonClasses()"
    >
      @if (isLoading()) {
      <svg
        class="animate-spin flex-shrink-0"
        [class]="iconClasses()"
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle
          class="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          stroke-width="4"
        ></circle>
        <path
          class="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        ></path>
      </svg>
      <span [class]="textClasses()">{{ loadingText() }}</span>
      } @else {
      <svg
        [class]="iconClasses()"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
        />
      </svg>
      <span [class]="textClasses()">{{ text() }}</span>
      }
    </button>
  `,
})
export class RefreshButtonComponent {
  // Input signals
  disabled = input<boolean>(false);
  text = input<string>('Refresh');
  loadingText = input<string>('Refreshing...');
  isLoading = input<boolean>(false);
  showText = input<boolean>(true);
  variant = input<'default' | 'primary' | 'secondary' | 'tertiary' | 'minimal'>(
    'default'
  );
  size = input<'sm' | 'md' | 'lg'>('md');
  iconOnly = input<boolean>(false);

  // Output signals
  refreshClick = output<void>();

  // Computed classes based on variant and size
  buttonClasses = computed(() => {
    const baseClasses =
      'inline-flex items-center border shadow-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50';

    const variantClasses = this.getVariantClasses();
    const sizeClasses = this.getSizeClasses();
    const shapeClasses = this.iconOnly() ? 'rounded-full' : 'rounded-md';

    return `${baseClasses} ${variantClasses} ${sizeClasses} ${shapeClasses}`;
  });

  iconClasses = computed(() => {
    const baseIconClasses = 'flex-shrink-0';
    const sizeIconClasses = this.getIconSizeClasses();
    const spacingClasses = this.showText() && !this.iconOnly() ? 'sm:mr-2' : '';

    return `${baseIconClasses} ${sizeIconClasses} ${spacingClasses} text-gray-600`;
  });

  textClasses = computed(() => {
    if (this.iconOnly()) {
      return 'sr-only';
    }
    return this.showText() ? 'hidden sm:inline' : 'sr-only';
  });

  private getVariantClasses(): string {
    switch (this.variant()) {
      case 'primary':
        return 'border-transparent bg-gray-600 text-white hover:bg-gray-700';
      case 'secondary':
        return 'border-gray-500 text-gray-500 bg-white hover:bg-gray-50';
      case 'tertiary':
        return 'border-slate-500 text-slate-500 bg-white hover:bg-slate-50';
      case 'minimal':
        return 'border-transparent text-gray-600 hover:text-gray-500 hover:bg-gray-50';
      default:
        return 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50';
    }
  }

  private getSizeClasses(): string {
    if (this.iconOnly()) {
      switch (this.size()) {
        case 'sm':
          return 'p-1.5';
        case 'lg':
          return 'p-3';
        default:
          return 'p-2';
      }
    }

    switch (this.size()) {
      case 'sm':
        return 'px-3 py-1.5 text-xs';
      case 'lg':
        return 'px-6 py-3 text-base';
      default:
        return 'px-4 py-2 text-sm';
    }
  }

  private getIconSizeClasses(): string {
    switch (this.size()) {
      case 'sm':
        return 'w-3 h-3';
      case 'lg':
        return 'w-5 h-5';
      default:
        return 'w-5 h-5';
    }
  }

  handleClick(): void {
    if (!this.disabled() && !this.isLoading()) {
      this.refreshClick.emit();
    }
  }
}
